import { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, FileText } from 'lucide-react';
import Button from '../../components/ui/Button';
import subscriptionService from '../../services/subscription';
import apiService from '../../services/api';
import type { SubscriptionPlan, LicenseType } from '../../types/subscription';

interface Organisation {
  id: number;
  name: string;
}

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  preselectedPlanId?: number;
  preselectedOrgId?: number;
  isSuperAdmin?: boolean;
  /** Activate for this org, with no picker — the billing console's org page. */
  lockedOrg?: { id: number; name: string };
}

/**
 * Manual activation — the platform billing path (super_admin or
 * billing_manager). Writes through POST /subscriptions/purchase, which raises
 * NO invoice: for comps, corrections and deals invoiced by hand. Customers
 * buy through batch-purchase and never see this.
 */
function ActivateSubscriptionModal({ onClose, onSuccess, preselectedPlanId, preselectedOrgId, isSuperAdmin, lockedOrg }: Props) {
  const pickOrg = !!isSuperAdmin && !lockedOrg;
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().split('T')[0];
  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const nextMonthStr = nextMonth.toISOString().split('T')[0];

  const [form, setForm] = useState({
    organisation_id: preselectedOrgId ? String(preselectedOrgId) : '',
    plan_id: preselectedPlanId ? String(preselectedPlanId) : '',
    payment_reference: '',
    start_date: today,
    end_date: nextMonthStr,
  });

  // Per-license-type seat counts keyed by type_key.
  const [seatCounts, setSeatCounts] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetches: Promise<any>[] = [
      subscriptionService.getAvailablePlans().then((res) => {
        if (res.success) setPlans(res.data);
      }),
    ];
    if (pickOrg) {
      fetches.push(
        apiService.get('/organisations?limit=200').then((res: any) => {
          if (res.success && Array.isArray(res.data?.organisations)) setOrganisations(res.data.organisations);
        })
      );
    }
    Promise.all(fetches).finally(() => setLoading(false));
  }, [pickOrg]);

  const selectedPlan = plans.find(p => p.id === parseInt(form.plan_id));
  const licenseTypes: LicenseType[] = selectedPlan?.license_types ?? [];

  // Initialise seat counts to 0 when plan changes.
  useEffect(() => {
    if (!selectedPlan) return;
    const init: Record<string, string> = {};
    for (const lt of licenseTypes) init[lt.type_key] = seatCounts[lt.type_key] ?? '0';
    setSeatCounts(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id]);

  const seatCost = licenseTypes.reduce((sum, lt) => {
    const count = parseInt(seatCounts[lt.type_key] || '0', 10) || 0;
    return sum + count * Number(lt.price_per_seat);
  }, 0);
  const totalSeats = licenseTypes.reduce((sum, lt) => sum + (parseInt(seatCounts[lt.type_key] || '0', 10) || 0), 0);
  const totalCost = (Number(selectedPlan?.base_price) || 0) + seatCost;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.plan_id) {
      setError('Please select a module plan');
      return;
    }
    if (pickOrg && !form.organisation_id) {
      setError('Please select an organisation');
      return;
    }

    const seats = licenseTypes
      .map(lt => ({ type_key: lt.type_key, total_seats: parseInt(seatCounts[lt.type_key] || '0', 10) || 0 }))
      .filter(s => s.total_seats > 0);

    if (seats.length === 0) {
      setError('Please allocate at least one seat to a license type');
      return;
    }

    setSubmitting(true);
    try {
      const res = await subscriptionService.purchaseSubscription({
        plan_id: parseInt(form.plan_id),
        seats,
        start_date: form.start_date,
        end_date: form.end_date,
        payment_reference: form.payment_reference || undefined,
        organisation_id: lockedOrg
          ? lockedOrg.id
          : pickOrg && form.organisation_id ? parseInt(form.organisation_id) : undefined,
      });
      if (res.success) {
        onSuccess();
      } else {
        // apiService wraps successful HTTP responses but with `success: false`
        // in the body (e.g. 201 with a business-level failure). Surface the
        // server's message if present.
        setError((res as any)?.error?.message || 'Failed to activate subscription');
      }
    } catch (err: any) {
      // The axios response interceptor flattens errors into
      // { message, status, errors } before rejecting, so the real message
      // lives on `err.message`, not `err.response.data.error.message`.
      setError(err?.message || err?.response?.data?.error?.message || 'Failed to activate subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <div className="bg-primary-100 p-2 rounded-lg">
              <FileText className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Manual Activation</h2>
              <p className="text-sm text-neutral-500">{lockedOrg ? `${lockedOrg.name} · ` : ''}No invoice is raised</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 text-primary-600 animate-spin" />
            </div>
          ) : (
            <>
              {/* Organisation selector (super admin, no fixed org) */}
              {pickOrg && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Organisation <span className="text-error-500">*</span>
                  </label>
                  <select
                    value={form.organisation_id}
                    onChange={e => setForm(f => ({ ...f, organisation_id: e.target.value }))}
                    required
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select an organisation...</option>
                    {organisations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Plan */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Module Plan <span className="text-error-500">*</span>
                </label>
                <select
                  value={form.plan_id}
                  onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}
                  required
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select a module...</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} — {subscriptionService.formatCurrency(plan.base_price)}/mo base ex GST
                    </option>
                  ))}
                </select>
              </div>

              {/* Per-license-type seat counts */}
              {selectedPlan && licenseTypes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Seats per License Type
                  </label>
                  <div className="space-y-2">
                    {licenseTypes.map(lt => (
                      <div key={lt.id} className="flex items-center gap-3 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-800">{lt.name}</p>
                          {lt.description && (
                            <p className="text-xs text-neutral-500 truncate">{lt.description}</p>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500 whitespace-nowrap">
                          {subscriptionService.formatCurrency(Number(lt.price_per_seat))}/seat ex GST
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={seatCounts[lt.type_key] ?? '0'}
                          onChange={e =>
                            setSeatCounts(s => ({ ...s, [lt.type_key]: e.target.value }))
                          }
                          className="w-20 border border-neutral-300 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedPlan && licenseTypes.length === 0 && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-700">
                  This plan has no license types defined. Contact support before activating.
                </div>
              )}

              {/* Invoice reference */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Invoice Reference
                </label>
                <input
                  type="text"
                  placeholder="e.g. INV-2024-001"
                  value={form.payment_reference}
                  onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Start Date <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                    required
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    End Date <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                    required
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Cost summary */}
              {selectedPlan && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-sm">
                  <div className="flex justify-between text-neutral-600 mb-1">
                    <span>Base price ex GST</span>
                    <span>{subscriptionService.formatCurrency(selectedPlan.base_price)}/mo</span>
                  </div>
                  <div className="flex justify-between text-neutral-600 mb-1">
                    <span>{totalSeats} seat{totalSeats !== 1 ? 's' : ''} ex GST</span>
                    <span>{subscriptionService.formatCurrency(seatCost)}/mo</span>
                  </div>
                  <div className="flex justify-between font-semibold text-neutral-900 border-t border-neutral-200 pt-2 mt-2">
                    <span>Total ex GST</span>
                    <span>{subscriptionService.formatCurrency(totalCost)}/mo</span>
                  </div>
                </div>
              )}
            </>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 pb-6">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit as any}
            disabled={submitting || loading}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Activating...
              </>
            ) : (
              'Activate Subscription'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ActivateSubscriptionModal;
